import type { LiveTrackPoint } from "@/features/deployment-dashboard/types";
import {
  worldToFloorplanPixel,
  type WorldToPixelTransformAsset,
} from "@/features/deployment-dashboard/widgets/panels/facility-schematic/services/world-to-pixel-transform";

export interface RawTrackPoint {
  id: string;
  areaId: string;
  objectId: string;
  x: number;
  y: number;
}

const FOOTPRINT_VERTEX_COUNT = 16;

/**
 * Traces a circular physical footprint (radius `radiusInMeters`, centered on
 * a world coordinate) into the facility schematic's pixel space, so its
 * on-screen size scales with the map's zoom instead of staying a fixed pixel
 * size. Shared by any point whose visual size should represent a real-world
 * physical extent (tracked-person dots, their trailing history dots, ...).
 */
export function computeFootprintPolygon(
  transform: Pick<WorldToPixelTransformAsset, "matrix">,
  worldX: number,
  worldY: number,
  radiusInMeters: number
): [number, number][] {
  return Array.from({ length: FOOTPRINT_VERTEX_COUNT }, (_, index) => {
    const angle = (index / FOOTPRINT_VERTEX_COUNT) * 2 * Math.PI;
    const vertex = worldToFloorplanPixel(
      transform,
      worldX + Math.cos(angle) * radiusInMeters,
      worldY + Math.sin(angle) * radiusInMeters
    );
    return [vertex.x, vertex.y] as [number, number];
  });
}

/**
 * Maps raw track positions (UTM 30N world coordinates, `source_frame:
 * "utm_30n_world"`) onto the facility schematic's own pixel space (the
 * floor plan image's own native pixel space), by applying the calibrated
 * affine `transform` (loaded via `useFacilitySchematicAssets`) to each point
 * independently (see `worldToFloorplanPixel`).
 *
 * Points are returned sorted by `id` so their order stays stable across updates
 * and ECharts can match each datum reliably.
 */
export function mapTrackPositionsToSchematicSpace(
  transform: Pick<WorldToPixelTransformAsset, "matrix">,
  points: RawTrackPoint[],
  pointRadiusInMeters: number
): LiveTrackPoint[] {
  return [...points]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((point) => ({
      id: point.id,
      areaId: point.areaId,
      objectId: point.objectId,
      worldX: point.x,
      worldY: point.y,
      ...worldToFloorplanPixel(transform, point.x, point.y),
      footprint: computeFootprintPolygon(transform, point.x, point.y, pointRadiusInMeters),
    }));
}
